















































import { Component, Vue, Prop, Watch, Emit } from 'vue-property-decorator';
import DraggableIcon from './DraggableIcon.vue';
import { TrainerResponseDTO } from '@/store/modules/jtms/trainer/trainer.types';
import SelectTrainerListRow from './SelectTrainerListRow.vue';
import TrainerDropdown from './TrainerDropdown.vue';
import { GroupLevelAttributeValue } from '@/jbi-shared/types/jaas-group-level-attributes.types';
import AttributeActionButtons from './AttributeActionButtons.vue';
import { deepCopy } from '@/jbi-shared/util/deep-copy.util';

@Component({
  components: {
    AttributeActionButtons,
    DraggableIcon,
    TrainerDropdown,
    SelectTrainerListRow
  }
})
export default class GroupLevelAttributeTrainerField extends Vue {
  @Prop() attributeValue!: GroupLevelAttributeValue;
  @Prop() fieldClasses!: any;
  @Prop() duplicationError!: boolean;
  @Prop() isSettings!: boolean;
  @Prop({ default: false }) overrideDisableDraggable!: boolean;
  @Prop({ default: false }) overrideDisableAttributeButton!: boolean;
  @Prop() deleteAttribute!: () => void;
  @Prop() updateAttributeValue!: (attribute: GroupLevelAttributeValue) => void;
  @Prop() toggleAttributeRequire!: (
    attribute: GroupLevelAttributeValue
  ) => void;
  @Prop() editAttribute!: (attribute: GroupLevelAttributeValue) => void;

  showField: boolean = false;
  fieldErrorMsg: string = '';
  selectedTrainers: TrainerResponseDTO[] = [];

  // Computed property so changes from the value from parent component are synced
  get isRequired() {
    return this.attributeValue.groupLevelAttributeSpec.isRequired;
  }

  @Watch('attributeValue.value', { deep: true })
  validateRequiredFieldValues(): void {
    // validate null / empty values for required fields
    if (
      !this.attributeValue.value ||
      (this.attributeValue.value as string[]).length === 0
    ) {
      this.fieldErrorMsg = `${this.attributeValue.label} is required.`;
      this.attributeValue.hasFieldError = true;
      this.updateAttributeValue(this.attributeValue);
      return;
    }

    this.fieldErrorMsg = '';
    this.attributeValue.hasFieldError = false;
    this.updateAttributeValue(this.attributeValue);
  }

  /**
   * A concatenated string of trainer full name and accreditation id (`trainerFullName.trainerId`
   * i.e. Raj Saha.1) will be stored in the attribute value.
   *
   * **RATIONALE**:
   * 1. Trainer data is already persisted in its own table (`jaas_trainer_accreditation`)
   * 2. The same trainer can have multiple accreditations while assigned to the same course
   * 3. Due to (2), simply storing the name value of the trainers is not sufficient to ensure
   *    correct relation between trainers and courses.
   * 4. Attribute values can only be stored as either `string`, `string[]` or custom GLA types
   *    in `jaas_group_level_attributes_values` table.
   * 5. Allow Course Settings to handle preselecting trainers (frontend filters a list of all
   *    trainers based on trainer id).
   * 6. Allow Course View to display ONLY trainer names.
   *
   * In order to maintain the proper references to the correct trainers accreditations
   * for each course, a few things can be done:
   * 1. Custom type to store the array of trainer accreditation objects.
   * 2. An array of concatenated strings from said trainer accreditation objects.
   *
   * **OPTION 1**
   * - Provides a simpler implementation for data retrieval, but it becomes complicated
   *   to maintain for updating data/references.
   *
   * **OPTION 2**
   * - Requires sanitization of trainer accreditation references to ensure the correct data
   *   is retrieved, which might become expensive at scale, but it simplifies the update
   *   process.
   *
   * Trainers accreditation objects retrieval is scoped to specific courses, so table
   * joins would only have to occur for single courses. Trainer accreditation objects are
   * fairly volatile with frequent updates to the values.
   *
   * As such, **OPTION 2** would be more maintainable.
   */
  @Watch('selectedTrainers')
  assignTrainer(newTrainers: TrainerResponseDTO[]) {
    this.selectedTrainers = newTrainers;

    const trainersFullNameWithId = this.selectedTrainers.map(
      (selectedTrainer: TrainerResponseDTO) => {
        return `${selectedTrainer.firstName} ${selectedTrainer.lastName}.${selectedTrainer.id}`;
      }
    );

    const clonedAttributeValue: GroupLevelAttributeValue = deepCopy(
      this.attributeValue
    );

    clonedAttributeValue.value = trainersFullNameWithId;

    this.updateAttributeValue(clonedAttributeValue);
    this.returnTrainerIds();
  }

  @Watch('isRequired', { immediate: true })
  validateRequired() {
    const attrValueAsString = this.attributeValue.value as string[];
    if (attrValueAsString && attrValueAsString.length > 0) {
      this.validateRequiredFieldValues();
      return;
    }

    this.attributeValue.hasFieldError = true;
    this.updateAttributeValue(this.attributeValue);
  }

  /**
   * Separate event to pass selected trainer id(s) to parent component.
   * Used for constructing course creation payload in `CourseLevelAttribute.vue`
   */
  @Emit('selected-trainers')
  returnTrainerIds() {
    return this.selectedTrainers.map((trainer) => trainer.id);
  }
}
